/**
 * Mystery Client Service
 *
 * Cross-project service that reads mystery_client data from the uploads API
 * to determine the client's city, coordinates, and zone GeoJSON.
 *
 * The mystery_client project stores its data at:
 *   {UPLOADS_API}/api/browse/mystery_client/{file}
 *
 * Key files:
 *   - clients.json        → maps companies to their JSON/GeoJSON files + cities
 *   - {company}.json      → company analytics data with `citta` object
 *   - zone/{City}_zona.geojson → city boundary polygons
 */
import { getAuthHeaders as sessionHeaders } from '../../../tools/session/clientSession';

const UPLOADS_BASE_URL =
  process.env.REACT_APP_UPLOADS_API_URL || 'https://uploads.traffixcloud.com';
const MC_PROJECT = 'mystery_client';

/**
 * City centre coordinates [lat, lng] — duplicated from mystery_client/constants/map.js
 * to avoid cross-project imports.
 */
export const MC_CITY_COORDS = {
  BERGAMO:  [45.6983, 9.6773],
  MESSINA:  [38.1931, 15.5552],
  CAGLIARI: [39.2238, 9.1217],
  LECCE:    [40.3515, 18.1750],
  SONDRIO:  [46.1677, 9.8677],
  BARI:     [41.1063, 16.8736],
  PALERMO:  [38.1157, 13.3615],
  MILANO:   [45.4642, 9.1900],
  ROMA:     [41.9028, 12.4964],
  TORINO:   [45.0703, 7.6869],
  BOLOGNA:  [44.4949, 11.3426],
  GENOVA:   [44.4056, 8.9463],
  COMO:     [45.8081, 9.0852],
  MODENA:   [44.6471, 10.9252],
  PIACENZA: [45.0519, 9.6933],
  'REGGIO EMILIA': [44.7038, 10.6328],
  'SAN DONA DI PIAVE': [45.6300, 12.5636],
};

// ── Internal helpers ────────────────────────────────────────────────────────
function getMcFileUrl(filePath) {
  const clean = filePath.startsWith('/') ? filePath.slice(1) : filePath;
  return `${UPLOADS_BASE_URL}/api/browse/${MC_PROJECT}/${clean}`;
}

function getAuthHeaders() {
  return sessionHeaders();
}

// ── Cache ───────────────────────────────────────────────────────────────────
let _clientsConfigCache = null;

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Fetch clients.json from the mystery_client uploads folder.
 * Returns an array of client configuration objects, each with:
 *   { company, jsonFile, geojsonFile?, logoFile?, cities? }
 */
export async function fetchMcClientsConfig() {
  if (_clientsConfigCache) return _clientsConfigCache;
  const url = getMcFileUrl('clients.json');
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`MC clients.json: ${res.status}`);
  const data = await res.json();
  // Support both a bare array and { clients: [...] } wrappers
  _clientsConfigCache = Array.isArray(data) ? data : (data?.clients || data?.data || []);
  return _clientsConfigCache;
}

/**
 * Find the client config entry that matches the given company name
 * (case-insensitive substring match — same logic as mystery_client project).
 */
export function findMcClientConfig(clientsConfig, companyName) {
  if (!clientsConfig || !companyName) return null;
  const upper = companyName.toUpperCase().trim();

  const exactMatch = clientsConfig.find((c) => c.company?.toUpperCase().trim() === upper);
  if (exactMatch) return exactMatch;

  const clientIdMatch = clientsConfig.find((c) => c.clientId?.toUpperCase().trim() === upper);
  if (clientIdMatch) return clientIdMatch;

  return clientsConfig.find((c) => {
    const cu = (c.company || '').toUpperCase().trim();
    return upper.includes(cu) || cu.includes(upper);
  });
}

/**
 * Fetch the company's analytics JSON from the mystery_client uploads folder.
 * Returns raw JSON (needs normalisation before use, but we only need `citta` keys).
 */
export async function fetchMcCompanyData(filePath) {
  const url = getMcFileUrl(filePath);
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`MC company data (${filePath}): ${res.status}`);
  return res.json();
}

/**
 * Fetch a zone GeoJSON file from the mystery_client uploads folder.
 * @param {string} zoneFilePath — e.g. "zone/Bergamo_zona.geojson"
 */
export async function fetchMcZoneGeoJSON(zoneFilePath) {
  const url = getMcFileUrl(zoneFilePath);
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`MC zone (${zoneFilePath}): ${res.status}`);
  return res.json();
}

/**
 * High-level helper: given a company name, resolve the first city, its
 * coordinates, and its zone GeoJSON (if available).
 *
 * Returns:
 *   { cityName, center: [lat, lng], zoneGeoJSON: GeoJSON | null, allCities: string[] }
 * or null if data cannot be resolved.
 */
/**
 * Derive [lat, lng] center from a GeoJSON FeatureCollection / Feature.
 */
function centerFromGeoJSON(geojson) {
  try {
    const features = geojson?.type === 'FeatureCollection'
      ? geojson.features
      : geojson?.type === 'Feature'
        ? [geojson]
        : null;
    if (!features?.length) return null;

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    const visit = (coords) => {
      if (!Array.isArray(coords) || coords.length === 0) return;
      if (typeof coords[0] === 'number') {
        const [lng, lat] = coords;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
        }
        return;
      }
      coords.forEach(visit);
    };
    features.forEach((f) => visit(f?.geometry?.coordinates));
    if (!Number.isFinite(minLat)) return null;
    return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
  } catch {
    return null;
  }
}

export async function resolveMysteryClientCity(companyName) {
  try {
    // 1. Fetch clients.json
    const configs = await fetchMcClientsConfig();
    const conf = findMcClientConfig(configs, companyName);
    if (!conf) {
      console.warn('[MC Service] No config found for', companyName);
      return null;
    }

    // 2. Resolve city names — prefer clients.json `cities`, then company JSON `citta`
    let allCities = [];
    if (conf.cities && typeof conf.cities === 'object') {
      allCities = Object.keys(conf.cities);
    }

    if (allCities.length === 0 && conf.jsonFile) {
      const rawData = await fetchMcCompanyData(conf.jsonFile);
      const cittaObj = rawData?.citta || rawData?.['città'] || {};
      allCities = Object.keys(cittaObj);
    }

    if (allCities.length === 0) {
      console.warn('[MC Service] No cities found in data for', companyName);
      return null;
    }

    // Use the first city
    const cityName = allCities[0];
    const cityUpper = cityName.toUpperCase();
    let center = MC_CITY_COORDS[cityUpper] || null;

    // 3. Zone GeoJSON is only needed to derive a centre for cities we have no
    // coordinates for — the map itself no longer draws it.
    let zoneGeoJSON = null;

    if (!center && conf.cities) {
      const cityKey = Object.keys(conf.cities).find(
        (k) => k.toUpperCase().trim() === cityUpper
      );
      if (cityKey && conf.cities[cityKey].zoneFile) {
        try {
          zoneGeoJSON = await fetchMcZoneGeoJSON(conf.cities[cityKey].zoneFile);
        } catch (e) {
          console.warn('[MC Service] Failed to fetch zone for', cityName, e);
        }
      }
    }

    // Fallback: try standard naming convention
    if (!center && !zoneGeoJSON) {
      const fallbackPath = `zone/${cityName}_zona.geojson`;
      try {
        zoneGeoJSON = await fetchMcZoneGeoJSON(fallbackPath);
      } catch {
        // Zone file may not exist for this city — that's fine
      }
    }

    if (!center && zoneGeoJSON) {
      center = centerFromGeoJSON(zoneGeoJSON);
    }

    return { cityName, center, zoneGeoJSON, allCities };
  } catch (err) {
    console.error('[MC Service] Failed to resolve mystery client city:', err);
    return null;
  }
}

/**
 * Resolve logo candidate paths the same way mystery_client does:
 *   {year}/{clientId}/{logoFile}
 *   company_logos/... (legacy)
 */
function getLogoCandidatePaths(clientConf, year) {
  if (!clientConf?.logoFile && !clientConf?.logo) return [];

  const raw = clientConf.logoFile || clientConf.logo || clientConf.logo_file || clientConf.logoPath;
  const baseName = raw.split('/').pop();
  const clientId = clientConf.clientId || clientConf.company?.toLowerCase();
  const paths = [];

  if (clientId && year) {
    paths.push(`${year}/${clientId}/${baseName}`);
  }
  if (raw.startsWith('company_logos/')) {
    paths.push(raw);
  }
  if (clientId && year && raw !== baseName) {
    paths.push(`${year}/${clientId}/${raw}`);
  }
  // Last-resort: raw path at project root (legacy layouts)
  paths.push(raw);
  paths.push(baseName);

  return [...new Set(paths)];
}

/**
 * Fetch years.json from mystery_client uploads (defaults to [2026]).
 */
async function fetchMcYears() {
  try {
    const url = getMcFileUrl('years.json');
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) return [2026];
    const data = await res.json();
    return Array.isArray(data) && data.length ? data : [2026];
  } catch {
    return [2026];
  }
}

/**
 * Fetch the mystery_client company logo and return a blob object URL,
 * mirroring getCompanyLogoWithAuth from the MC project.
 *
 * Reads clients.json (cached) → finds logoFile for companyName
 * → tries {year}/{clientId}/{logoFile} (and fallbacks) with Bearer auth
 * → returns blob URL string (or null on failure).
 *
 * Caller is responsible for calling URL.revokeObjectURL() on cleanup.
 */
export async function fetchMcCompanyLogoUrl(companyName) {
  try {
    const configs = await fetchMcClientsConfig();
    const conf = findMcClientConfig(configs, companyName);
    if (!conf) {
      console.warn('[MC Service] No client config for', companyName, '— known companies:', configs.map((c) => c.company));
      return null;
    }

    const years = await fetchMcYears();
    // Prefer newest year first (same as MC typically uses selectedYear)
    const yearsToTry = [...years].sort((a, b) => Number(b) - Number(a));

    const candidates = [];
    for (const year of yearsToTry) {
      candidates.push(...getLogoCandidatePaths(conf, year));
    }
    // Deduplicate while preserving order
    const uniquePaths = [...new Set(candidates)];

    let lastError = null;
    for (const path of uniquePaths) {
      try {
        const url = getMcFileUrl(path);
        console.log('[MC Service] Fetching logo from', url);
        const res = await fetch(url, { headers: getAuthHeaders() });
        if (!res.ok) {
          lastError = new Error(`MC logo (${path}): ${res.status}`);
          continue;
        }
        const blob = await res.blob();
        if (!blob.size) {
          lastError = new Error(`Empty logo (${path})`);
          continue;
        }
        const imageBlob = blob.type.startsWith('image/')
          ? blob
          : new Blob([blob], { type: 'image/png' });
        return URL.createObjectURL(imageBlob);
      } catch (err) {
        lastError = err;
      }
    }

    console.warn('[MC Service] Failed to fetch company logo:', lastError);
    return null;
  } catch (err) {
    console.warn('[MC Service] Failed to fetch company logo:', err);
    return null;
  }
}
